import { supabase } from '../lib/supabase';

// Normalize error to always be a proper Error instance
function normalizeError(err, fallback) {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const msg = err?.message || fallback || 'Unknown error';
    return Object.assign(new Error(msg), err);
  }
  return new Error(err || fallback || 'Unknown error');
}

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toCamelCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) return obj?.map(item => toSnakeCase(item));
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Map lowercase service values to DB Title Case enum values
const STATUS_MAP = {
  'new': 'New Lead',
  'contacted': 'Contacted',
  'qualified': 'Qualified',
  'proposal_sent': 'Proposal Sent',
  'negotiating': 'Negotiating',
  'won': 'Converted',
  'lost': 'Lost',
  'on_hold': 'On Hold',
};

const STATUS_REVERSE_MAP = {
  'New Lead': 'new',
  'Contacted': 'contacted',
  'Qualified': 'qualified',
  'Proposal Sent': 'proposal_sent',
  'Negotiating': 'negotiating',
  'Converted': 'won',
  'Lost': 'lost',
  'On Hold': 'on_hold',
};

const COMPANY_TYPE_MAP = {
  'general_contractor': 'Commercial',
  'subcontractor': 'Commercial',
  'developer': 'Commercial',
  'owner': 'Commercial',
  'government': 'Government',
  'nonprofit': 'Non-Profit',
  'residential': 'Residential',
  'commercial': 'Commercial',
  'industrial': 'Industrial',
  'other': 'Other',
};

const COMPANY_TYPE_REVERSE_MAP = {
  'Commercial': 'commercial',
  'Residential': 'residential',
  'Industrial': 'industrial',
  'Government': 'government',
  'Non-Profit': 'nonprofit',
  'Other': 'other',
};

const SOURCE_MAP = {
  'referral': 'Referral',
  'website': 'Website',
  'cold_call': 'Cold Call',
  'trade_show': 'Trade Show',
  'linkedin': 'Social Media',
  'email_campaign': 'Email Campaign',
  'advertisement': 'Advertisement',
  'existing_customer': 'Referral',
  'other': 'Other',
};

const SOURCE_REVERSE_MAP = {
  'Referral': 'referral',
  'Website': 'website',
  'Cold Call': 'cold_call',
  'Trade Show': 'trade_show',
  'Advertisement': 'advertisement',
  'Social Media': 'linkedin',
  'Email Campaign': 'email_campaign',
  'Other': 'other',
};

const INTERACTION_TYPE_MAP = {
  'call': 'Call',
  'email': 'Email',
  'meeting': 'Meeting',
  'site_visit': 'Site Visit',
  'proposal': 'Proposal',
  'demo': 'Demo',
  'follow_up': 'Follow-up',
  'other': 'Note',
};

const INTERACTION_TYPE_REVERSE_MAP = {
  'Call': 'call',
  'Email': 'email',
  'Meeting': 'meeting',
  'Proposal': 'proposal',
  'Site Visit': 'site_visit',
  'Demo': 'demo',
  'Follow-up': 'follow_up',
  'Note': 'other',
};

const FOLLOWUP_STATUS_MAP = {
  'scheduled': 'Scheduled',
  'completed': 'Completed',
  'cancelled': 'Cancelled',
  'overdue': 'Overdue',
};

const FOLLOWUP_STATUS_REVERSE_MAP = {
  'Scheduled': 'scheduled',
  'Completed': 'completed',
  'Cancelled': 'cancelled',
  'Overdue': 'overdue',
};

// Normalize a contact record from DB to service format
const normalizeContact = (c) => {
  if (!c) return c;
  const camel = toCamelCase(c);
  return {
    ...camel,
    firstName: camel?.firstName || (camel?.fullName ? camel?.fullName?.split(' ')?.[0] : ''),
    lastName: camel?.lastName || (camel?.fullName ? camel?.fullName?.split(' ')?.slice(1)?.join(' ') : ''),
  };
};

const normalizeFollowup = (f) => {
  if (!f) return f;
  const camel = toCamelCase(f);
  const rawStatus = camel?.followupStatus || camel?.status;
  const rawType = camel?.interactionType || camel?.followupType;
  return {
    ...camel,
    followupStatus: FOLLOWUP_STATUS_REVERSE_MAP?.[rawStatus] || rawStatus?.toLowerCase() || 'scheduled',
    interactionType: INTERACTION_TYPE_REVERSE_MAP?.[rawType] || rawType?.toLowerCase() || 'call',
    scheduledAt: camel?.scheduledAt,
  };
};

// Normalize a prospect record from DB to service format
const normalizeProspect = (p) => {
  if (!p) return p;
  const camel = toCamelCase(p);
  return {
    ...camel,
    status: STATUS_REVERSE_MAP?.[camel?.status] || camel?.status?.toLowerCase()?.replace(/ /g, '_') || 'new',
    companyType: COMPANY_TYPE_REVERSE_MAP?.[camel?.companyType] || camel?.companyType?.toLowerCase() || 'other',
    source: SOURCE_REVERSE_MAP?.[camel?.source] || camel?.source?.toLowerCase()?.replace(/ /g, '_') || 'other',
    prospectContacts: camel?.prospectContacts?.map(normalizeContact),
    prospectFollowups: camel?.prospectFollowups?.map(normalizeFollowup),
  };
};

const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase?.auth?.getUser();
  if (error || !user) throw new Error('User not authenticated');
  return user;
};

export const prospectService = {
  // ── PROSPECTS ──────────────────────────────────────────────────────────
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('prospects')?.select(`
          *,
          prospect_contacts(id, full_name, first_name, last_name, email, phone, title, is_primary),
          prospect_followups(id, title, status, followup_status, scheduled_at)
        `)?.order('created_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        const dbStatus = STATUS_MAP?.[filters?.status] || filters?.status;
        query = query?.eq('status', dbStatus);
      }
      if (filters?.companyType && filters?.companyType !== 'all') {
        const dbType = COMPANY_TYPE_MAP?.[filters?.companyType] || filters?.companyType;
        query = query?.eq('company_type', dbType);
      }
      if (filters?.source && filters?.source !== 'all') {
        const dbSource = SOURCE_MAP?.[filters?.source] || filters?.source;
        query = query?.eq('source', dbSource);
      }
      if (filters?.search) {
        query = query?.ilike('company_name', `%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) return { data: null, error };
      return { data: data?.map(normalizeProspect), error: null };
    } catch (err) {
      console.error('Error fetching prospects:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('prospects')?.select(`
          *,
          prospect_contacts(*),
          prospect_interactions(*),
          prospect_followups(*)
        `)?.eq('id', id)?.maybeSingle();

      if (error) return { data: null, error };
      return { data: normalizeProspect(data), error: null };
    } catch (err) {
      console.error('Error fetching prospect:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  async create(prospectData) {
    try {
      const user = await getCurrentUser();
      const dbStatus = STATUS_MAP?.[prospectData?.status] || 'New Lead';
      const dbType = COMPANY_TYPE_MAP?.[prospectData?.companyType] || 'Other';
      const dbSource = SOURCE_MAP?.[prospectData?.source] || 'Other';

      const payload = {
        company_name: prospectData?.companyName,
        company_type: dbType,
        status: dbStatus,
        source: dbSource,
        website: prospectData?.website || null,
        address: prospectData?.address || null,
        city: prospectData?.city || null,
        state: prospectData?.state || null,
        zip_code: prospectData?.zipCode || null,
        estimated_value: prospectData?.estimatedValue ? parseFloat(prospectData?.estimatedValue) : null,
        probability: prospectData?.probability ? parseInt(prospectData?.probability) : 0,
        notes: prospectData?.notes || null,
        created_by: user?.id,
      };

      const { data, error } = await supabase?.from('prospects')?.insert(payload)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeProspect(data), error: null };
    } catch (err) {
      console.error('Error creating prospect:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  async update(id, prospectData) {
    try {
      const payload = {};
      if (prospectData?.companyName !== undefined) payload.company_name = prospectData?.companyName;
      if (prospectData?.companyType !== undefined) payload.company_type = COMPANY_TYPE_MAP?.[prospectData?.companyType] || prospectData?.companyType;
      if (prospectData?.status !== undefined) payload.status = STATUS_MAP?.[prospectData?.status] || prospectData?.status;
      if (prospectData?.source !== undefined) payload.source = SOURCE_MAP?.[prospectData?.source] || prospectData?.source;
      if (prospectData?.website !== undefined) payload.website = prospectData?.website;
      if (prospectData?.address !== undefined) payload.address = prospectData?.address;
      if (prospectData?.city !== undefined) payload.city = prospectData?.city;
      if (prospectData?.state !== undefined) payload.state = prospectData?.state;
      if (prospectData?.zipCode !== undefined) payload.zip_code = prospectData?.zipCode;
      if (prospectData?.estimatedValue !== undefined) payload.estimated_value = prospectData?.estimatedValue ? parseFloat(prospectData?.estimatedValue) : null;
      if (prospectData?.probability !== undefined) payload.probability = prospectData?.probability ? parseInt(prospectData?.probability) : 0;
      if (prospectData?.notes !== undefined) payload.notes = prospectData?.notes;

      const { data, error } = await supabase?.from('prospects')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeProspect(data), error: null };
    } catch (err) {
      console.error('Error updating prospect:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('prospects')?.delete()?.eq('id', id);
      if (error) return { error };
      return { error: null };
    } catch (err) {
      console.error('Error deleting prospect:', err?.message);
      return { error: normalizeError(err) };
    }
  },

  async convertToCustomer(prospectId, customerData) {
    try {
      const user = await getCurrentUser();
      const customerPayload = toSnakeCase({ ...customerData, createdBy: user?.id });
      const { data: customer, error: customerError } = await supabase?.from('customers')?.insert(customerPayload)?.select()?.single();
      if (customerError) return { data: null, error: customerError };

      const { data: prospect, error: prospectError } = await supabase?.from('prospects')?.update({
          status: 'Converted',
          converted_to_customer_id: customer?.id,
          converted_at: new Date()?.toISOString()
        })?.eq('id', prospectId)?.select()?.single();
      if (prospectError) return { data: null, error: prospectError };

      return { data: { customer: toCamelCase(customer), prospect: normalizeProspect(prospect) }, error: null };
    } catch (err) {
      console.error('Error converting prospect:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  // ── CONTACTS ───────────────────────────────────────────────────────────
  async getContacts(prospectId) {
    try {
      const { data, error } = await supabase?.from('prospect_contacts')?.select('*')?.eq('prospect_id', prospectId)?.order('is_primary', { ascending: false });
      if (error) return { data: null, error };
      return { data: data?.map(normalizeContact), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async createContact(contactData) {
    try {
      const user = await getCurrentUser();
      const firstName = contactData?.firstName || '';
      const lastName = contactData?.lastName || '';
      const fullName = `${firstName} ${lastName}`?.trim();

      const payload = {
        prospect_id: contactData?.prospectId,
        full_name: fullName,
        first_name: firstName,
        last_name: lastName,
        title: contactData?.title || null,
        email: contactData?.email || null,
        phone: contactData?.phone || null,
        is_primary: contactData?.isPrimary || false,
        notes: contactData?.notes || null,
        created_by: user?.id,
      };

      const { data, error } = await supabase?.from('prospect_contacts')?.insert(payload)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeContact(data), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async updateContact(id, contactData) {
    try {
      const payload = {};
      if (contactData?.firstName !== undefined || contactData?.lastName !== undefined) {
        const firstName = contactData?.firstName || '';
        const lastName = contactData?.lastName || '';
        payload.first_name = firstName;
        payload.last_name = lastName;
        payload.full_name = `${firstName} ${lastName}`?.trim();
      }
      if (contactData?.fullName !== undefined) payload.full_name = contactData?.fullName;
      if (contactData?.title !== undefined) payload.title = contactData?.title;
      if (contactData?.email !== undefined) payload.email = contactData?.email;
      if (contactData?.phone !== undefined) payload.phone = contactData?.phone;
      if (contactData?.isPrimary !== undefined) payload.is_primary = contactData?.isPrimary;
      if (contactData?.notes !== undefined) payload.notes = contactData?.notes;

      const { data, error } = await supabase?.from('prospect_contacts')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeContact(data), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async deleteContact(id) {
    try {
      const { error } = await supabase?.from('prospect_contacts')?.delete()?.eq('id', id);
      return { error: error || null };
    } catch (err) {
      return { error: normalizeError(err) };
    }
  },

  // ── INTERACTIONS ───────────────────────────────────────────────────────
  async getInteractions(prospectId) {
    try {
      const { data, error } = await supabase?.from('prospect_interactions')?.select('*')?.eq('prospect_id', prospectId)?.order('interaction_date', { ascending: false });
      if (error) return { data: null, error };
      return {
        data: data?.map(i => {
          const camel = toCamelCase(i);
          return {
            ...camel,
            interactionType: INTERACTION_TYPE_REVERSE_MAP?.[camel?.interactionType] || camel?.interactionType?.toLowerCase() || 'other',
          };
        }),
        error: null
      };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async createInteraction(interactionData) {
    try {
      const user = await getCurrentUser();
      const dbType = INTERACTION_TYPE_MAP?.[interactionData?.interactionType] || 'Note';

      const payload = {
        prospect_id: interactionData?.prospectId,
        interaction_type: dbType,
        subject: interactionData?.subject,
        notes: interactionData?.notes || null,
        outcome: interactionData?.outcome || null,
        interaction_date: interactionData?.interactionDate || new Date()?.toISOString(),
        duration_minutes: interactionData?.durationMinutes || null,
        contact_id: interactionData?.contactId || null,
        created_by: user?.id,
      };

      const { data, error } = await supabase?.from('prospect_interactions')?.insert(payload)?.select()?.single();
      if (error) return { data: null, error };
      return { data: toCamelCase(data), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async deleteInteraction(id) {
    try {
      const { error } = await supabase?.from('prospect_interactions')?.delete()?.eq('id', id);
      return { error: error || null };
    } catch (err) {
      return { error: normalizeError(err) };
    }
  },

  // ── FOLLOW-UPS ─────────────────────────────────────────────────────────
  async getFollowups(prospectId) {
    try {
      const { data, error } = await supabase?.from('prospect_followups')?.select('*')?.eq('prospect_id', prospectId)?.order('scheduled_at', { ascending: true });
      if (error) return { data: null, error };
      return { data: data?.map(normalizeFollowup), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async getAllFollowups(filters = {}) {
    try {
      let query = supabase?.from('prospect_followups')?.select('*, prospects(company_name)')?.order('scheduled_at', { ascending: true });

      if (filters?.status && filters?.status !== 'all') {
        const dbStatus = FOLLOWUP_STATUS_MAP?.[filters?.status] || filters?.status;
        query = query?.eq('status', dbStatus);
      }
      if (filters?.startDate) {
        query = query?.gte('scheduled_at', filters?.startDate);
      }
      if (filters?.endDate) {
        query = query?.lte('scheduled_at', filters?.endDate);
      }

      const { data, error } = await query;
      if (error) return { data: null, error };
      return { data: data?.map(normalizeFollowup), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async createFollowup(followupData) {
    try {
      const user = await getCurrentUser();
      const dbStatus = FOLLOWUP_STATUS_MAP?.[followupData?.followupStatus || followupData?.status] || 'Scheduled';
      const dbType = INTERACTION_TYPE_MAP?.[followupData?.interactionType || followupData?.followupType] || 'Call';

      const payload = {
        prospect_id: followupData?.prospectId,
        title: followupData?.title,
        description: followupData?.description || null,
        followup_type: dbType,
        interaction_type: dbType,
        status: dbStatus,
        followup_status: dbStatus,
        scheduled_at: followupData?.scheduledAt,
        duration_minutes: followupData?.durationMinutes || 30,
        contact_id: followupData?.contactId || null,
        assigned_to: followupData?.assignedTo || user?.id,
        created_by: user?.id,
      };

      const { data, error } = await supabase?.from('prospect_followups')?.insert(payload)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeFollowup(data), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async updateFollowup(id, followupData) {
    try {
      const payload = {};
      if (followupData?.title !== undefined) payload.title = followupData?.title;
      if (followupData?.description !== undefined) payload.description = followupData?.description;
      if (followupData?.scheduledAt !== undefined) payload.scheduled_at = followupData?.scheduledAt;
      if (followupData?.durationMinutes !== undefined) payload.duration_minutes = followupData?.durationMinutes;
      if (followupData?.contactId !== undefined) payload.contact_id = followupData?.contactId;
      if (followupData?.completedAt !== undefined) payload.completed_at = followupData?.completedAt;
      if (followupData?.completionNotes !== undefined) payload.completion_notes = followupData?.completionNotes;
      if (followupData?.followupStatus !== undefined || followupData?.status !== undefined) {
        const rawStatus = followupData?.followupStatus || followupData?.status;
        const dbStatus = FOLLOWUP_STATUS_MAP?.[rawStatus] || rawStatus;
        payload.status = dbStatus;
        payload.followup_status = dbStatus;
      }
      if (followupData?.interactionType !== undefined || followupData?.followupType !== undefined) {
        const rawType = followupData?.interactionType || followupData?.followupType;
        const dbType = INTERACTION_TYPE_MAP?.[rawType] || rawType;
        payload.followup_type = dbType;
        payload.interaction_type = dbType;
      }

      const { data, error } = await supabase?.from('prospect_followups')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) return { data: null, error };
      return { data: normalizeFollowup(data), error: null };
    } catch (err) {
      return { data: null, error: normalizeError(err) };
    }
  },

  async deleteFollowup(id) {
    try {
      const { error } = await supabase?.from('prospect_followups')?.delete()?.eq('id', id);
      return { error: error || null };
    } catch (err) {
      return { error: normalizeError(err) };
    }
  },

  // ── ANALYTICS ──────────────────────────────────────────────────────────
  async getAnalytics() {
    try {
      const { data: prospects, error } = await supabase?.from('prospects')?.select('status, company_type, source, estimated_value, probability, lead_score, created_at, converted_at');

      if (error) return { data: null, error };

      const { data: followups } = await supabase?.from('prospect_followups')?.select('status, followup_status, scheduled_at, followup_type, interaction_type');

      const { data: interactions } = await supabase?.from('prospect_interactions')?.select('interaction_type, interaction_date');

      return {
        data: {
          prospects: prospects?.map(normalizeProspect) || [],
          followups: followups?.map(normalizeFollowup) || [],
          interactions: interactions?.map(i => {
            const camel = toCamelCase(i);
            return {
              ...camel,
              interactionType: INTERACTION_TYPE_REVERSE_MAP?.[camel?.interactionType] || camel?.interactionType?.toLowerCase() || 'other',
            };
          }) || []
        },
        error: null
      };
    } catch (err) {
      console.error('Error fetching analytics:', err?.message);
      return { data: null, error: normalizeError(err) };
    }
  },

  // ── REAL-TIME ──────────────────────────────────────────────────────────
  subscribeToChanges(callback) {
    return supabase?.channel('prospects_changes')?.on('postgres_changes', { event: '*', schema: 'public', table: 'prospects' }, callback)?.subscribe();
  },

  unsubscribe(channel) {
    if (channel) supabase?.removeChannel(channel);
  }
};
